<script setup lang="ts">
import type { ExtendedLocationDetail } from '@/types/location';
import type { ErrorCollection } from '@/utils/validator';
import type { TranslationFormItem } from '@/types/translation';

const mapOptions = {
	fields: ['geometry', 'name', 'formatted_address'],
	strictBounds: false,
	types: ['(cities)'],
	componentRestrictions: { country: 'jp' },
};

const props = defineProps({
	data: {
		type: Object as PropType<TranslationFormItem>,
		default: () => {},
		required: true,
	},
	destinationIndex: {
		type: Number,
		default: null,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	errors: {
		type: Array as PropType<Array<ErrorCollection>>,
		required: true,
		default: () => [],
	},
});

const emit = defineEmits<{
	(e: 'onChange', data: TranslationFormItem): void
	(e: 'onRemove'): void
	(e: 'onPressEnter'): void
}>();

const { isMobile } = useWindowResize();

const formData = reactive<TranslationFormItem>({
	arrive: '',
	depart: '',
	location: '',
	experiences: {
		tradition: false,
		food: false,
		nature: false,
		anime: false,
		workshop: false,
	},
	coordinates: null,
	countryCode: '',
});

const dateRange = ref<string[]>([]);

const isFirstItem = computed(() => props.destinationIndex === 0);
const userTimezone = computed(() => getTimezone());
const dateErrorMessage = computed(() => {
	const arriveErrorMessage = getValidationErrorMessageCollection(
		props.errors,
		props.destinationIndex,
		'arrive',
	);
	if (arriveErrorMessage) {
		return arriveErrorMessage;
	}

	const departErrorMessage = getValidationErrorMessageCollection(
		props.errors,
		props.destinationIndex,
		'depart',
	);

	if (departErrorMessage) {
		return departErrorMessage;
	}

	return '';
});

const arriveDateErrorObj = computed(() => props.errors?.[props.destinationIndex]?.arrive);
const departDateErrorObj = computed(() => props.errors?.[props.destinationIndex]?.depart);
const dateRangeErrorObj = computed(() => {
	const errors = [];
	if (arriveDateErrorObj.value) {
		errors.push(...arriveDateErrorObj.value);
	}
	if (departDateErrorObj.value) {
		errors.push(...departDateErrorObj.value);
	}
	return errors;
});
const locationErrorMessage = computed(() => getValidationErrorMessageCollection(props.errors, props.destinationIndex, 'location'));

function handleDateRangeChange(date: string | string[]) {
	formData.arrive = date?.[0];
	formData.depart = date?.[1];

	if (typeof date === 'object' && date) {
		dateRange.value = date;
	}
}

function handleLocationSelected(location: ExtendedLocationDetail) {
	formData.location = location.name;
	formData.coordinates = location.coordinate;
	formData.countryCode = location.countryCode || '';
}

// Watch for changes in the props.formData and update formData
watch(
	() => props.data,
	(newData) => {
		formData.arrive = newData.arrive;
		formData.depart = newData.depart;
		formData.location = newData.location;
		formData.experiences = newData.experiences;
		formData.coordinates = newData.coordinates;
		formData.countryCode = newData.countryCode;
	},
);

watch(
	() => formData,
	() => {
		emit('onChange', formData);
	},
	{ deep: true },
);

// Assign props.data to formData initially
onMounted(() => {
	if (props.data) {
		formData.arrive = props.data.arrive;
		formData.depart = props.data.depart;
		formData.location = props.data.location;
		formData.experiences = props.data.experiences;
		formData.coordinates = props.data.coordinates;
		formData.countryCode = props.data.countryCode;
	}
});
</script>

<template>
  <div class="destination-container">
    <VDivider
      v-if="!isFirstItem"
      class="divider"
    />
    <div class="destination-input">
      <div class="destination-header">
        <p class="header-title">
          Destination {{ destinationIndex === 0 ? '' : destinationIndex + 1 }}
        </p>
        <BaseButton
          v-if="!isFirstItem"
          class="remove-btn"
          variant="link"
          color="primary"
          size="sm"
          @click="$emit('onRemove')"
        >
          Remove
        </BaseButton>
      </div>
      <div class="form-items">
        <label for="daterange">Travel dates</label>
        <div class="date-group-container">
          <BaseDateTimePicker
            id="daterange"
            v-model="dateRange"
            :date="dateRange"
            :timezone="userTimezone"
            :date-errors="dateRangeErrorObj"
            :is-teleport-date-picker="isMobile"
            date-placeholder="Arrival - Departure dates"
            is-multi-calendar-range
            is-date-only
            @on-date-change="handleDateRangeChange"
          />
        </div>
        <p
          v-if="dateErrorMessage"
          class="error-message"
        >
          {{ dateErrorMessage }}
        </p>
      </div>

      <div class="form-items">
        <label for="location">Which city are you visiting?</label>
        <BaseGoogleAutoComplete
          id="location"
          v-model="formData.location"
          placeholder="Tokyo, Japan"
          :error-message="locationErrorMessage"
          :options="mapOptions"
          @on-select-location="handleLocationSelected"
          @enter="emit('onPressEnter')"
        />
      </div>

      <div class="form-items">
        <label>What kind of local experiences do you want to have?</label>
        <div class="exp-container">
          <BaseCheckbox
            :id="`${destinationIndex}-food`"
            v-model="formData.experiences.food"
            class="exp-option no-select cursor-pointer"
            :label="DISCOVERY_JAPAN_CATEGORY.FOOD"
          />
          <BaseCheckbox
            :id="`${destinationIndex}-anime`"
            v-model="formData.experiences.anime"
            class="exp-option no-select cursor-pointer"
            :label="DISCOVERY_JAPAN_CATEGORY.ANIME"
          />
          <BaseCheckbox
            :id="`${destinationIndex}-tradition`"
            v-model="formData.experiences.tradition"
            class="exp-option no-select cursor-pointer"
            :label="DISCOVERY_JAPAN_CATEGORY.CULTURE"
          />
          <BaseCheckbox
            :id="`${destinationIndex}-workshop`"
            v-model="formData.experiences.workshop"
            class="exp-option no-select cursor-pointer"
            :label="DISCOVERY_JAPAN_CATEGORY.WORKSHOP"
          />
          <BaseCheckbox
            :id="`${destinationIndex}-nature`"
            v-model="formData.experiences.nature"
            class="exp-option no-select cursor-pointer"
            :label="DISCOVERY_JAPAN_CATEGORY.NATURE"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.destination-container {
  display: flex;
  flex-direction: column;

  .divider {
    margin: spacings-get(8) 0;
  }

  .destination-input {
    display: flex;
    flex-direction: column;
    gap: spacings-get(4);
  }

  .destination-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .header-title {
      @include fonts-get(semibold, text-md);
      color: colors-get(gray, 700);
    }

    .remove-btn {
      padding: 0;
      color: colors-get(error, 500);
      @include fonts-get(regular, text-sm);
    }
  }

  .form-items {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: rem(6);
    @include fonts-get(medium, text-sm);

    label {
      color: colors-get(gray, 600);
    }
  }

  .date-group-container {
    display: flex;
    gap: spacings-get(2);

    :deep(.date-time-group) {
      width: 100%;
    }
  }

  .exp-container {
    display: flex;
    flex-direction: column;
    gap: spacings-get(2);

    .exp-option {
      width: fit-content;
    }
  }
}
</style>
